import React, {createContext, useCallback, useContext, useMemo, useRef, ReactNode} from 'react';
import {useAgentDrawer} from '../hooks/useAgentDrawer';

/** Records that a report-intent chat message has become a real, stored report. */
type ReportIntentSavedRecorder = (messageId: string, savedReportId: string) => void;

type AgentDrawerContextValue = {
	isOpen: boolean;
	inputValue: string;
	savedPromptTexts: ReadonlySet<string>;
	openDrawer: () => void;
	closeDrawer: () => void;
	setInputValue: (value: string) => void;
	setSavedPromptTexts: (texts: ReadonlySet<string>) => void;
	/** Called by the report builder once a chat-proposed report has been saved for the first time, so
	 * the originating chat card can stop reopening its own (now stale) payload as a fresh draft and
	 * navigate to the stored report instead. A no-op until the chat panel has registered itself. */
	recordReportIntentSaved: ReportIntentSavedRecorder;
	/** Registers the chat panel's own recorder. Returns an unsubscribe. The drawer's chat panel owns
	 * the message list, but the builder is rendered outside the drawer and has no path to it, so the
	 * two meet here rather than through a prop chain. */
	registerReportIntentSavedRecorder: (recorder: ReportIntentSavedRecorder) => () => void;
};

const AgentDrawerContext = createContext<AgentDrawerContextValue | null>(null);

type AgentDrawerProviderProps = {
	children: ReactNode;
};

export const AgentDrawerProvider = ({children}: AgentDrawerProviderProps): JSX.Element => {
	const {isOpen, openDrawer, closeDrawer} = useAgentDrawer();
	const [inputValue, setInputValue] = React.useState('');
	const [savedPromptTexts, setSavedPromptTexts] = React.useState<ReadonlySet<string>>(new Set());

	// A ref, not state: re-registering must not re-render every consumer of this context, and the
	// recorder is only ever read at call time.
	const reportIntentSavedRecorderRef = useRef<ReportIntentSavedRecorder | null>(null);

	const registerReportIntentSavedRecorder = useCallback((recorder: ReportIntentSavedRecorder) => {
		reportIntentSavedRecorderRef.current = recorder;
		return () => {
			// Guarded so a later registration is not torn down by an earlier one's cleanup.
			if (reportIntentSavedRecorderRef.current === recorder) {
				reportIntentSavedRecorderRef.current = null;
			}
		};
	}, []);

	const recordReportIntentSaved = useCallback<ReportIntentSavedRecorder>((messageId, savedReportId) => {
		reportIntentSavedRecorderRef.current?.(messageId, savedReportId);
	}, []);

	const value = useMemo(
		() => ({
			isOpen,
			openDrawer,
			closeDrawer,
			inputValue,
			setInputValue,
			savedPromptTexts,
			setSavedPromptTexts,
			recordReportIntentSaved,
			registerReportIntentSavedRecorder,
		}),
		[
			isOpen,
			openDrawer,
			closeDrawer,
			inputValue,
			setInputValue,
			savedPromptTexts,
			setSavedPromptTexts,
			recordReportIntentSaved,
			registerReportIntentSavedRecorder,
		]
	);

	return <AgentDrawerContext.Provider value={value}>{children}</AgentDrawerContext.Provider>;
};

export const useAgentDrawerContext = (): AgentDrawerContextValue => {
	const ctx = useContext(AgentDrawerContext);
	if (!ctx) throw new Error('useAgentDrawerContext must be used within AgentDrawerProvider');
	return ctx;
};

/** Non-throwing variant, for components that are rendered inside the drawer's provider in the app but
 * are also mounted standalone (tests, Storybook) and only use the drawer opportunistically. */
export const useAgentDrawerContextOptional = (): AgentDrawerContextValue | null => useContext(AgentDrawerContext);
